import { faqsPl } from "@data/firmware-landing/faq";
import {
  AlertCircle,
  Bluetooth,
  Calendar,
  CheckCircle,
  Code,
  Cpu,
  FileText,
  Globe,
  Layers,
  LifeBuoy,
  MapPin,
  PenTool,
  PlaySquare,
  Radio,
  RefreshCw,
  Search,
  Shield,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Target,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";

import type { AboutUsCopy } from "@components/bluetooth-landing/AboutUs";
import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "Firmware BLE - programowanie urządzeń Bluetooth Low Energy";
export const description =
  "Jesteśmy firmą programistyczną specjalizującą się w firmware BLE na chipsety Nordic nRF52, nRF53 i nRF54. Piszemy firmware produkcyjny oraz towarzyszącą aplikację mobilną – pełny stack od chipa po sklep z aplikacjami.";

export const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Blues Brackets",
    "url": "https://bluesbrackets.com",
    "logo": "https://bluesbrackets.com/favicon-dark.png",
    description,
    "areaServed": "EU",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "sales",
      "email": "hello@bluesbrackets.com",
      "availableLanguage": ["Polish", "English"],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Programowanie firmware embedded dla urządzeń Bluetooth Low Energy",
    "provider": {
      "@type": "Organization",
      "name": "Blues Brackets",
    },
    description,
    "areaServed": "EU",
    "serviceType": "Embedded firmware development",
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqsPl.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a,
      },
    })),
  },
];

export const heroCopy: HeroCopy = {
  header: "Firmware dla urządzeń",
  headerTail: "Bluetooth\xa0LE",
  description:
    "Stworzymy Twój firmware na Nordic nRF52, nRF53 i nRF54 – oraz towarzyszącą aplikację mobilną. Jeden zespół odpowiedzialny za cały stack, od profilu GATT po App Store.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Poznaj nasz proces",
    href: "#proces",
  },
  benefits: [
    { text: "nRF52\xa0nRF53\xa0nRF54", Icon: CheckCircle },
    {
      text: "Firmware i aplikacja w jednym zespole",
      Icon: CheckCircle,
    },
    { text: "Gotowe na RODO i rynek UE", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Dla kogo jest ta oferta?",
    p: "Pracujemy z zespołami hardware'owymi, startupami i firmami produktowymi, które potrzebują niezawodnego firmware embedded oraz otaczającego go software'u. Czy któryś z tych punktów pasuje do Twojej sytuacji?",
  },
  points: [
    {
      Icon: Cpu,
      title: "Budujesz nowy produkt BLE od zera",
      description:
        "Wybrałeś już chip Nordic i potrzebujesz zespołu firmware, który zna nRF Connect SDK, Zephyr RTOS i stos BLE od podszewki.",
    },
    {
      Icon: AlertCircle,
      title: "Twój obecny firmware jest niestabilny",
      description:
        "Gubione połączenia, niespodziewane resety albo kod, którego nikt nie chce dotykać. Diagnozujemy przyczynę problemu i przywracamy nad nim kontrolę.",
    },
    {
      Icon: Smartphone,
      title: "Potrzebujesz firmware i aplikacji mobilnej",
      description:
        "Chcesz jednego partnera odpowiedzialnego za obie strony łącza BLE – żeby profil GATT, projekt protokołu i logika aplikacji były spójne od pierwszego dnia.",
    },
    {
      Icon: RefreshCw,
      title: "Migrujesz na nRF53 lub nRF54",
      description:
        "Przejście z serii nRF52 na nRF5340 lub nRF54L15 wiąże się z nowym toolchainem i zmianami architektonicznymi. Pomagamy zaplanować i przeprowadzić migrację.",
    },
    {
      Icon: Target,
      title: "Potrzebujesz aktualizacji OTA na produkcji",
      description:
        "Wysyłanie aktualizacji firmware do urządzeń będących już w terenie wymaga bezpiecznego bootloadera, protokołu DFU i aplikacji mobilnej, która zarządza tym procesem niezawodnie.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Opowiedz nam o swoim sprzęcie i o tym, co ma robić firmware. Pomożemy oszacować zakres prac i dobrać właściwe podejście.",
    href: "#kontakt",
    buttonText: "Umów darmową konsultację",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego warto zlecić nam napisanie firmware BLE?",
    p: "Specjalizujemy się w firmware Bluetooth LE na sprzęcie Nordic Semiconductors. Budujemy też aplikację mobilną. Większość firm embedded kończy pracę na urządzeniu – my dowozimy kompletny produkt.",
    imageAlt: "Programowanie firmware embedded na sprzęcie Nordic nRF",
  },
  values: [
    {
      Icon: Cpu,
      title: "Specjaliści Nordic nRF52, nRF53, nRF54",
      description:
        "Na co dzień pracujemy z nRF Connect SDK, Zephyr RTOS i stosem BLE Nordic. nRF52832, nRF52840, nRF5340, nRF54L15, nRF54H20 – znamy ich różnice, zestawy peryferiów i pułapki, które potrafią kosztować tygodnie pracy przy uruchamianiu.",
    },
    {
      Icon: Bluetooth,
      title: "Głęboka znajomość Bluetooth LE",
      description:
        "Własne profile GATT, dostrajanie parametrów połączenia, optymalizacja energetyczna, OTA DFU, BLE Mesh i Auracast (LE Audio). Projektujemy warstwę protokołu BLE tak, żeby działała niezawodnie w realnym świecie, a nie tylko na biurku.",
    },
    {
      Icon: Smartphone,
      title: "Pełny stack: firmware i aplikacja mobilna",
      description:
        "Projektujemy profil GATT, a następnie wdrażamy go zarówno w firmware, jak i w aplikacji iOS/Android. Zero ryzyka na przekazaniu między zespołami – jeden partner odpowiedzialny za cały produkt BLE.",
    },
    {
      Icon: Shield,
      title: "Jakość gotowa na produkcję",
      description:
        "Piszemy firmware, który ma trafić do produkcji seryjnej i działać bez nadzoru w terenie. To oznacza bezpieczne bootloadery, obsługę watchdoga, systematyczne profilowanie poboru energii i solidne testy na fizycznym sprzęcie.",
    },
    {
      Icon: Globe,
      title: "Europejski zespół, standardy danych UE",
      description:
        "Działamy z Polski i rozumiemy otoczenie regulacyjne dla produktów sprzedawanych w UE. Chmura zgodna z RODO, komunikacja po polsku i angielsku oraz zgodność z wymaganiami CE i Bluetooth SIG.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Programowanie firmware na zamówienie",
    p: "Od architektury i projektu GATT po testy produkcyjne i aktualizacje OTA. Nasza usługa programowania firmware embedded obejmuje każdy etap cyklu życia firmware.",
  },
  services: [
    {
      Icon: Search,
      title: "Architektura firmware i konsulting",
      description:
        "Analizujemy projekt Twojego sprzętu, definiujemy architekturę firmware, dobieramy konfigurację RTOS-a i projektujemy profil GATT BLE, zanim powstanie pierwsza linijka kodu – ograniczając ryzyko od samego początku.",
    },
    {
      Icon: Code,
      title: "Programowanie firmware na zamówienie",
      description:
        "Piszemy firmware produkcyjny na Nordic nRF52, nRF53 i nRF54, korzystając z nRF Connect SDK i Zephyr RTOS. Sterowniki peryferiów, logika aplikacji, konfiguracja stosu BLE i zarządzanie energią.",
    },
    {
      Icon: Bluetooth,
      title: "Integracja Bluetooth LE",
      description:
        "Własne profile GATT, konfiguracja połączenia i rozgłaszania, parowanie i bonding, obsługa powiadomień i indykacji oraz BLE Mesh lub Auracast dla bardziej złożonych topologii.",
    },
    {
      Icon: Zap,
      title: "Aktualizacje firmware przez OTA (DFU)",
      description:
        "Wdrażamy Nordic Secure Bootloader oraz protokół nRF DFU, dzięki czemu możesz wysyłać aktualizacje firmware do urządzeń w terenie za pomocą towarzyszącej aplikacji mobilnej – bezpiecznie i bez fizycznego dostępu.",
    },
    {
      Icon: Smartphone,
      title: "Aplikacja mobilna towarzysząca urządzeniu",
      description:
        "Tworzymy aplikację iOS i Android (Flutter), która paruje się z Twoim urządzeniem. Jeden zespół odpowiada za protokół GATT po obu stronach, dzięki czemu integracja działa niezawodnie od pierwszego dnia.",
    },
    {
      Icon: Layers,
      title: "Uruchamianie i testy sprzętu",
      description:
        "Uruchamiamy nowe rewizje PCB, piszemy warstwy abstrakcji sprzętowej dla niestandardowych peryferiów i budujemy zautomatyzowane stanowiska testowe weryfikujące działanie firmware na realnym sprzęcie.",
    },
    {
      Icon: TrendingUp,
      title: "Optymalizacja energetyczna",
      description:
        "Czas pracy na baterii to cecha produktu. Profilujemy pobór prądu, dostrajamy interwały rozgłaszania, wdrażamy tryby uśpienia i mierzymy efekt na realnym sprzęcie – nie tylko w symulacji.",
    },
    {
      Icon: LifeBuoy,
      title: "Utrzymanie i wsparcie firmware",
      description:
        "Po premierze zajmujemy się bieżącym utrzymaniem firmware, aktualizacjami zgodności z nowymi wersjami nRF SDK i Zephyr, poprawkami błędów oraz iteracyjnym rozwojem funkcji wraz z rozwojem Twojego produktu.",
    },
  ],
  cta: {
    h4: "Nie wiesz, czego dokładnie potrzebujesz?",
    p: "Prześlij nam specyfikację sprzętu i krótki opis wymagań dla firmware. Odezwiemy się z uczciwą oceną i propozycją zakresu prac.",
    button: {
      text: "Umów darmową konsultację",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak wygląda proces tworzenia firmware BLE?",
    p: "Przejrzyste etapy, jasno określone rezultaty i testy od pierwszego dnia. Zobacz, jak przechodzimy od pierwszego kontaktu do firmware gotowego na produkcję.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Darmowe spotkanie online",
      duration: "w kilka godzin",
      description:
        "30 minut rozmowy z inżynierem firmware. Opisujesz sprzęt i wymagania, a my dajemy uczciwą pierwszą ocenę i orientacyjną wycenę.",
    },
    {
      Icon: FileText,
      title: "Warsztaty architektoniczne",
      duration: "w kilka dni",
      description:
        "Po 2-godzinnej sesji online otrzymujesz pisemny raport obejmujący architekturę firmware, projekt profilu GATT, obszary ryzyka oraz szczegółową wycenę projektu.",
    },
    {
      Icon: Search,
      title: "Uruchomienie sprzętu",
      duration: "1-2 tygodnie",
      description:
        "Weryfikujemy sprzęt, uruchamiamy podstawowy szkielet firmware, potwierdzamy komunikację BLE i dokumentujemy interfejsy peryferyjne przed rozpoczęciem pełnego rozwoju.",
    },
    {
      Icon: PenTool,
      title: "Podpisanie umowy",
      duration: "ustalenie harmonogramu",
      description:
        "Ustalamy kamienie milowe, zobowiązujemy się do terminów dostaw i uzgadniamy sposób komunikacji postępów przez cały projekt.",
    },
    {
      Icon: Code,
      title: "Etapowe programowanie firmware",
      duration: "min. 2-3 miesiące",
      description:
        "Sterowniki peryferiów, stos BLE, logika aplikacji, OTA DFU i optymalizacja energetyczna – dostarczane etapami, z testami na realnym sprzęcie i regularnymi przeglądami postępów.",
    },
    {
      Icon: PlaySquare,
      title: "Integracja aplikacji mobilnej",
      duration: "równolegle lub po kolei",
      description:
        "Towarzysząca aplikacja iOS i Android powstaje równolegle z firmware lub po nim, korzystając z tego samego projektu profilu GATT, dzięki czemu obie strony działają ze sobą poprawnie.",
    },
    {
      Icon: ShoppingBag,
      title: "Testy produkcyjne i publikacja w sklepach",
      duration: "min. 2 tygodnie",
      description:
        "Wspieramy procedury testów produkcyjnych, walidację DFU oraz publikację towarzyszącej aplikacji w App Store i Google Play.",
    },
    {
      Icon: CheckCircle,
      title: "Przekazanie i wsparcie",
      duration: "na stałe",
      description:
        "Otrzymujesz pełny kod źródłowy i dokumentację, albo kontynuujemy współpracę jako Twój długoterminowy partner od utrzymania firmware.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Zamów projekt firmware BLE",
  },
};

export const aboutUsCopy: AboutUsCopy = {
  header: {
    h2: "O nas",
    h3: "Firma od firmware, która dowozi też aplikację",
    p1: "Jesteśmy małym, doświadczonym zespołem z Polski z praktycznym doświadczeniem w firmware embedded, Bluetooth Low Energy i aplikacjach mobilnych.",
    p2: "Michał i Tomasz prowadzą rozwój firmware i aplikacji mobilnych, Kuba odpowiada za backend i infrastrukturę, a Mateusz projektuje UX, który sprawia, że produkty sprzętowe są intuicyjne w obsłudze.",
    p3: "Zgodność z RODO",
    p4: "Dane w UE",
    Icon: ShieldCheck,
    imageAlt: "Zespół programujący firmware na sprzęcie Nordic nRF",
  },
  values: [
    {
      Icon: Users,
      title: "Pracujesz bezpośrednio z inżynierami",
      description:
        "Bez account managerów i warstw outsourcingu. Osoba na rozmowie to osoba, która pisze firmware.",
    },
    {
      Icon: MapPin,
      title: "Europejski zespół, regulacje UE",
      description:
        "Działamy z Polski, rozumiemy wymagania CE, zgodność z RODO i to, czego potrzeba, by wprowadzić produkt sprzętowy na rynek UE.",
    },
    {
      Icon: Radio,
      title: "Pokrywamy firmware i cały stack",
      description:
        "Od doboru chipa nRF i firmware, przez aplikację mobilną, po backend – jeden partner dla kompletnego produktu BLE wyraźnie zmniejsza ryzyko integracji.",
    },
  ],
  cta: {
    text: "Napisz do nas",
    href: "#kontakt",
  },
};

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o programowanie firmware",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Szukasz firmy do programowania firmware?",
    p: "Umów się na darmową konsultację, opisz swój sprzęt i wymagania, a otrzymasz uczciwą pierwszą ocenę od doświadczonego inżyniera firmware.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Świadczymy usługi programowania firmware embedded dla urządzeń Bluetooth Low Energy na Nordic nRF52, nRF53 i nRF54 – wraz z towarzyszącą aplikacją mobilną. Działamy z Polski, obsługujemy klientów w całej Europie.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "/projects", text: "Projekty" },
      { href: "/blog", text: "Blog" },
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      { href: "/aplikacje-bluetooth", text: "Aplikacje mobilne Bluetooth" },
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [
      {
        href: "/pl/blogs/is_bluetooth_le_right_for_your_product",
        text: "Czy Bluetooth LE to właściwa technologia do realizacji Twojego produktu lub MVP?",
      },
      {
        href: "/pl/blogs/bluetooth_le_product_architecture",
        text: "Architektura produktu opartego o Bluetooth LE",
      },
      {
        href: "/pl/blogs/ble_mobile_application_support",
        text: "Wsparcie BLE po stronie aplikacji mobilnej",
      },
      {
        href: "/pl/blogs/ble_embedded_development_platforms",
        text: "Najlepsze platformy rozwojowe embedded z Bluetooth LE",
      },
      {
        href: "/pl/blogs/auracast_mcu",
        text: "ESP32 Auracast: czy działa? Najlepsze MCU do LE Audio",
      },
      {
        href: "/pl/blogs/ble_hardware_2026",
        text: "Najlepszy hardware do rozwoju Bluetooth Low Energy BLE w 2026 roku",
      },
    ],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: {
    heading: "Napisz do nas",
    fields: {
      name: {
        label: "Imię i nazwisko",
        placeholder: "Jan Kowalski",
        requiredError: "Podaj imię i nazwisko",
      },
      email: {
        label: "Adres e-mail",
        placeholder: "jan@firma.pl",
        requiredError: "Podaj adres e-mail",
        invalidError: "Podaj poprawny adres e-mail",
      },
      topic: {
        label: "Opis projektu",
        placeholder:
          "Krótko opisz swój sprzęt, użyty chip i to, co ma robić firmware...",
        requiredError: "Opisz swój projekt",
        maxLengthError: "Opis może mieć maksymalnie 2000 znaków",
      },
    },
    submit: "Wyślij zapytanie",
    submitting: "Wysyłanie...",
    successMessage: "Wiadomość wysłana!",
    errorMessage: "Nie udało się wysłać wiadomości :(",
    responseTime: "Odpowiemy w ciągu 24 godzin w dni robocze.",
    emailSubjectPrefix: "Imię i nazwisko",
  },
};
